<template>
    <div>
        <div class="row m-b-10">
            <div class="col-md-12">
                
                <div class="pull-right">
                    <a class="btn btn-block btn-outline btn-info btn-xs"
                        :href="adjustmentMatrixUrl"
                    >
                        {{$t("analysis:tabs:adjustment_matrix")}}
                    </a>
                </div>
                <div class="pull-right">
                    <a class="btn btn-block btn-outline btn-info btn-xs"
                        href="#"
                        v-on:click.prevent="getSelectedComparables(true)"
                    >
                        {{$t("analysis:ct:recreate_comparable_transactions")}}
                    </a>
                </div>

                <h4>{{$t("analysis:ct:selected")}} <help-icon slug="dolocanje-pogojev-iskanja-primerljivih-transakcij" /></h4>

                <results-table
                    :session="session_data"
                    :show_main_property_row="selected_comparables_table_items && selected_comparables_table_items.length > 0"
                    :items="selected_comparables_table_items"
                    :loading="loading_selected_comparables"
                    :hide_columns="hide_results_columns"
                    :pagination="false"
                    :sortable="true"
                    :no_items_text="$t('analysis:ct:no_comparable_selected')"
                    :show_deselect_controls="true"
                    :selected_comparable_ids="selected_comparables_comparables_ids"
                    :selected_wide_set_ids="selected_comparables_wide_set_ids"
                    @comparable-clicked="handleComparableTransactionClicked"
                    @deselect-all="handleDeselectAll"
                    ref="comparables_table"
                >
                    <template v-slot:additional-rows="slotProps" v-if="selected_comparables_wide_set_ids.length > 0">
                        <tr>
                            <td class="p-10" :colspan="slotProps.colspan">
                                <a href="#" @click.prevent="toggleShowWideSet">
                                    {{show_wide_set ? $t("general:hide") : $t("general:show")}} {{selected_comparables_wide_set_ids.length}} {{$t("analysis:ct:transactions_in_wide_set")}}
                                </a>
                            </td>
                        </tr>
                    </template>
                </results-table>
            </div>
        </div>

        <div class="row">
            <div class="col-md-8 vld-parent" style="min-height:300px;">
                <h4>{{$t("analysis:ct:comparable_search_conditions")}}</h4>
                <loading 
                    :active="loading_form_data"
                    :can-cancel="false" 
                    :is-full-page="false"
                />
                
                <search-form
                    v-if="form_data"
                    v-model="form_data"
                    :main_property_gps="main_property_gps"
                    :hide_search_options="hide_search_options"
                    :radius_editable="radius_editable"
                    @radius-input="handleRadiusInput"
                    @radius-blur="applyDefaultRadius"
                    @submit="getSearchResults(true)"
                    @reset="handleReset"
                />

            </div>

            <div class="col-md-4" style="min-height:300px;">
                <loading 
                    :active="loading_session_data || loading_search_results"
                    :can-cancel="false" 
                    :is-full-page="false"
                />
                <results-map
                    v-if="main_property_gps"
                    :main_marker="main_property_gps"
                    :center="main_property_gps"
                    :markers="markers"
                    :min-zoom="3"
                    :geoms="form_data ? form_data.geoms : null"
                    @update:drawn_geoms="handleGeomsUpdate"
                    ref="map"
                >
                    <template v-slot:details-control="slotProps">
                        <results-table
                            :session="session_data"
                            :show_main_property_row="true"
                            :hide_columns="hide_results_columns"
                            :items="[slotProps.selected_item]"
                            :loading="slotProps.loading_selected_item"
                            :pagination="false"
                            @comparable-clicked="handleComparableTransactionClicked"
                            @close-fullscreen="closeFullscreen"
                        />
                    </template>
                </results-map>
            </div>

        </div>

        <div class="row m-t-20">
            <div class="col-md-12">
                <h4>{{$t("general:search:results")}}</h4>
                <results-table
                    :session="session_data"
                    :show_main_property_row="true"
                    :hide_columns="hide_results_columns"
                    :items="filtered_search_results"
                    :loading="loading_search_results"
                    :sortable="true"
                    no_items_text="Ni rezultatov iskanja"
                    :show_deselect_controls="true"
                    :selected_comparable_ids="selected_comparables_comparables_ids"
                    :selected_wide_set_ids="selected_comparables_wide_set_ids"
                    @comparable-clicked="handleComparableTransactionClicked"
                    @deselect-all="handleDeselectAll"
                    ref="search_results_table"
                />
            </div>
        </div>
    </div>
</template>

<script>
import Loading from 'vue-loading-overlay';
import 'vue-loading-overlay/dist/vue-loading.css';

import ApiService from "@/components/utils/api.service.js";
import { Swal } from "@/components/utils/swal.alerts.js";

import SearchForm from "./components/SearchForm.vue";
import ResultsTable from "./components/ResultsTable.vue";
import ResultsMap from "./components/ResultsMap.vue";

import store from "./store";
import HelpIcon from '@/components/widgets/HelpIcon.vue';
import { searchCircleRadius, isRadiusEditable } from '@/components/utils/geometry.js';

export default {
    store: store,
    props: {
        session_id: {
            type: Number,
            required: true
        },
        transaction_type: {
            type: String,
            default: "sale",
            validator: function (value) {
                return ["sale", "rent"].includes(value);
            },
        },
        valuation_method: {
            type: String,
            default: "comparables",
            validator: function (value) {
                return ["comparables", "cost"].includes(value);
            },
        },
    },
    components: {
        Loading,
        SearchForm,
        ResultsMap,
        "results-table": ResultsTable,
        HelpIcon,
    },
    data: function () {
        return {
            loading_session_data: false,
            session_data: null,

            form_data: null,
            search_results: [],
            selected_comparables: [],

            loading_selected_comparables: false,
            loading_form_data: true,
            loading_search_results: true,

            show_wide_set: false,
            hide_results_columns: [],
            hide_search_options: [],

            radius_input_timer: null,
        }
    },
    methods: {
        getSearchResults: function(save_search_params) {
            if (this.applyDefaultRadius()) {
                this.$nextTick(function() { this.getSearchResults(save_search_params); }.bind(this));
                return;
            }
            if (this.radius_input_timer) {
                // Apply a still-debounced radius first; the map reports the new circle on the next tick
                this.applyRadiusInput();
                this.$nextTick(function() { this.getSearchResults(save_search_params); }.bind(this));
                return;
            }
            this.loading_search_results = true;
            var payload = Object.assign({}, this.form_data);
            ApiService
                .post(`/amas/analysis/avm/api/${this.session_id}/search`,
                    payload,
                    {params: { save_search_params: save_search_params, valuation_method: this.valuation_method }}
                )
                .then(response => {
                    this.search_results = response.data;
                    this.$refs.search_results_table.page = 1; // Reset pagination
                })
                .catch(error => {
                    if(error.response && error.response.status===400) {
                        this.handleBadRequestResponse(error.response.data)
                    } else {
                        throw error;
                    }
                })
                .then(() => {
                    this.loading_search_results = false;
                });
        },
        getSelectedComparables: function(recreate = false) {
            this.loading_selected_comparables = true;
            ApiService
                .get(`/amas/analysis/analysis/api/${this.session_id}/transactions`, { params: {
                    comparable: false,
                    valuation_method: this.valuation_method,
                    transaction_type: this.transaction_type,
                    recreate: recreate,
                }})
                .then(response => {
                    this.selected_comparables = response.data;
                })
                .then(() => {
                    this.loading_selected_comparables = false;
                });
        },
        getSessionFormData: function(reset_search_params) {
            this.loading_form_data = true;
            return ApiService
                .get(`/amas/analysis/avm/api/${this.session_id}/search-parameters`, { params: {
                    valuation_method: this.valuation_method,
                    transaction_type: this.transaction_type,
                    reset_search_params: reset_search_params,
                }})
                .then(response => {
                    this.form_data = response.data;
                    return
                })
                .then(() => {
                    this.loading_form_data = false;
                });
        },
        handleBadRequestResponse: function(errors) {
            let text = "";
            Object.keys(errors).forEach(function(key) {
                text += key + ": " + errors[key] + "\n";
            });
            alert(text);
        },
        _isInProgress: function(item) {
            return this.$store.state.inprogressTransactionItemsIds.includes(item.id);
        },
        _isFailed: function(item) {
            return this.$store.state.failedTransactionItemsIds.includes(item.id)
        },
        _isSelectedComparable: function(item) {
            if (this.selected_comparables_comparables_ids.includes(item.id)) {
                return true;
            } else if (this.selected_comparables_wide_set_ids.includes(item.id)) {
                return false;
            } else {
                return null;
            }
        },
        addOrUpdateItemToSelected: function(item) {
            let objIndex = this.selected_comparables.findIndex((obj => obj.id == item.id));
            if (objIndex == -1) {
                this.selected_comparables.push(item);
            } else {
                this.$set(this.selected_comparables, objIndex, item)
            }
        },
        handleComparableTransactionClicked: function({item, selection}) {
            if(this._isInProgress(item) || this._isFailed(item)) {return;}
            this.$store.commit("inprogressTransactionItemsIdsAdd", item.id);

            let action, comparable;
            if (item.comparable==null) {
                // Add to selected transactions 
                action = "add";
                comparable = selection==="comparable";
            } else {
                if ( selection==="wide_set" ) {// Remove transaction
                    action = "remove";
                }
                if ( selection==="comparable" && item.comparable===false ) { // Add to comparables
                    action = "add";
                    comparable = true;
                } 
                if ( selection==="comparable" && item.comparable===true ) { // Remove from comparables, keep in wide set
                    action = "add";
                    comparable = false;
                }
            }

            ApiService
                .post(`/amas/analysis/avm/api/${this.session_id}/transactions/manage`, null, {
                    params: { sale_item_id: item.id, comparable: comparable, action: action, valuation_method: this.valuation_method }
                })
                .then(response => {
                    if (action==="remove") {
                        this.selected_comparables = this.selected_comparables.filter(i => i.sale_object.id != item.id);
                    } else {
                        this.addOrUpdateItemToSelected(response.data);
                    }

                    // If the map has a selected item, update it.
                    if (this.$refs.map && this.$refs.map.selected_item && this.$refs.map.selected_item.id === item.id) {
                        const isComparable = action === "remove" && selection === "wide_set" ? null : response.data.comparable;
                        this.$refs.map.selected_item.comparable = isComparable;
                    }
                })
                .catch(error => {
                    this.$store.commit("failedTransactionItemsIdsAdd", item.id);
                    throw error;
                })
                .then(() => {
                    this.$store.commit("inprogressTransactionItemsIdsRemove", item.id);
                })
        },

        getSessionData: function(){
            this.loading_session_data = true;
            ApiService
                .get(`/amas/analysis/analysis/api/${this.session_id}`)
                .then(response => {
                    this.session_data = response.data;
                })
                .catch(error => {
                    throw error;
                })
                .then(() => {
                    this.loading_session_data = false;
                })
        },

        handleDeselectAll: function(selection) {
            const confirmText = selection === "comparable"
                ? this.$t("analysis:ct:deselect_all_comparables_confirm")
                : this.$t("analysis:ct:deselect_all_confirm");
            Swal({
                title: this.$t("general:are_you_sure"),
                text: confirmText,
                icon: "warning",
                showCancelButton: true,
                confirmButtonColor: "#2cabe3",
                confirmButtonText: this.$t("general:yes_confirm"),
                cancelButtonText: this.$t("general:no_cancel"),
            }).then((val) => {
                if (val.isConfirmed) {
                    if (selection === "comparable") {
                        this.demoteAllComparables();
                    } else {
                        this.deselectAllComparables();
                    }
                }
            });
        },
        demoteAllComparables: function() {
            const comparableIds = this.selected_comparables_comparables_ids;
            this.$store.commit("inprogressTransactionItemsIdsBulkAdd", comparableIds);
            ApiService
                .post(`/amas/analysis/avm/api/${this.session_id}/transactions/manage`, null, {
                    params: { action: "demote_all_comparables", valuation_method: this.valuation_method }
                })
                .then(() => {
                    this.selected_comparables = this.selected_comparables.map(item => ({
                        ...item,
                        comparable: false,
                    }));
                })
                .catch(error => {
                    throw error;
                })
                .then(() => {
                    this.$store.commit("inprogressTransactionItemsIdsBulkRemove", comparableIds);
                });
        },
        deselectAllComparables: function() {
            const allItemIds = this.filtered_selected_comparables.map(obj => obj.id);
            this.$store.commit("inprogressTransactionItemsIdsBulkAdd", allItemIds);
            ApiService
                .post(`/amas/analysis/avm/api/${this.session_id}/transactions/manage`, null, {
                    params: { action: "remove_all", valuation_method: this.valuation_method }
                })
                .then(() => {
                    this.selected_comparables = [];
                })
                .catch(error => {
                    throw error;
                })
                .then(() => {
                    this.$store.commit("inprogressTransactionItemsIdsBulkRemove", allItemIds);
                });
        },
        toggleShowWideSet: function(){
            this.show_wide_set = !this.show_wide_set;
        } ,

        fetchUserComparableSearchPermissions() {
          const that = this;
          ApiService
            .get("/api/v1/current-user")
            .then(res => {
                const permList = res.data.all_permissions_list;

                const results_permissions = [
                    "analysis.comparable_search__results__show_transaction_id",
                    "analysis.comparable_search__results__show_address",
                    "analysis.comparable_search__results__show_transaction_date",
                    "analysis.comparable_search__results__show_component_size",
                    "analysis.comparable_search__results__show_sizes",
                    "analysis.comparable_search__results__show_building_year_built",
                    "analysis.comparable_search__results__show_transaction_amount_gross",
                    "analysis.comparable_search__results__show_transaction_amount_m2",
                    "analysis.comparable_search__results__show_match_score",
                    "analysis.comparable_search__results__show_options"
                ]

                results_permissions.forEach( function (permission){
                  if (!permList.includes(permission)){
                    that.hide_results_columns.push(permission.substring(22))
                  }
                });

                const search_parameter_permissions = [
                    "analysis.comparable_search__by_rent_or_sale",
                    "analysis.comparable_search__transaction_dates",
                    "analysis.comparable_search__location",
                    "analysis.comparable_search__unit_size",
                    "analysis.comparable_search__parcel_size",
                    "analysis.comparable_search__year_built",
                    "analysis.comparable_search__transaction_amount",
                    "analysis.comparable_search__real_estate_type",
                    "analysis.comparable_search__unit_type",
                    "analysis.comparable_search__apartment_count",
                    "analysis.comparable_search__construction_phase",
                    "analysis.comparable_search__max_size",
                    "analysis.comparable_search__parcel_dedicated_usage",
                    "analysis.comparable_search__parcel_actual_usage",
                    "analysis.comparable_search__parcel_land_quality",
                    "analysis.comparable_search__sale_share",
                    "analysis.comparable_search__valuation_zone",
                    "analysis.comparable_search__transaction_type",
                    "analysis.comparable_search__transaction_volume",
                    "analysis.comparable_search__exclude_outliers",
                    "analysis.comparable_search__low_quality",
                    "analysis.comparable_search__consider_spatial_plan_area",
                    "analysis.comparable_search__property_model_type",
                    "analysis.price_zone_permission",
                ]

                search_parameter_permissions.forEach( function (permission){
                  if (!permList.includes(permission)){
                    that.hide_search_options.push(permission)
                  }
                });
            })
            .catch(error => {
                throw error;
            });
        },
        handleReset: function() {
            if (this.$refs.map) {
                this.$refs.map.clearSavedGeomLayers();
            }
            this.getSessionFormData(true);
        },
        handleGeomsUpdate: function(newGeoms) {
            if (this.form_data) {
                this.$set(this.form_data, 'geoms',
                    Object.keys(newGeoms).length > 0 ? newGeoms : null);
                this.$set(this.form_data, 'radius', searchCircleRadius(newGeoms));
            }
        },
        handleRadiusInput: function() {
            clearTimeout(this.radius_input_timer);
            this.radius_input_timer = setTimeout(this.applyRadiusInput, 400);
        },
        // Fills in the default radius when no search area is set; returns whether it did.
        applyDefaultRadius: function() {
            if (!this.form_data || !this.radius_editable || this.form_data.radius || !this.form_data.default_radius) return false;
            this.$set(this.form_data, 'radius', this.form_data.default_radius);
            this.applyRadiusInput();
            return true;
        },
        applyRadiusInput: function() {
            clearTimeout(this.radius_input_timer);
            this.radius_input_timer = null;
            var radius = this.form_data && this.form_data.radius;
            if (this.$refs.map && (radius === null || (typeof radius === "number" && radius > 0))) {
                this.$refs.map.setSearchRadius(radius);
            }
        },
        closeFullscreen() {
            this.$refs.map.mapExitFullscreen();
        },
    },
    mounted() {
        this.$store.dispatch("retrieveMetaOptions");

        this.fetchUserComparableSearchPermissions();

        this.getSessionData();
        // 1. Get sessions's form data
        this.getSessionFormData()
            .then(() => {
                // 3. Submit search form
                this.getSearchResults();
            });
        // 2. Get selected comparables
        this.getSelectedComparables();
    },
    computed: {
        radius_editable: function() {
            return isRadiusEditable(this.form_data ? this.form_data.geoms : null);
        },
        selected_comparables_comparables_ids: function() {
            return this.filtered_selected_comparables.filter(obj => obj.comparable==true).map(obj => obj.id);
        },
        selected_comparables_wide_set_ids: function() {
            return this.filtered_selected_comparables.filter(obj => obj.comparable==false).map(obj => obj.id);
        },

        selected_comparables_table_items: function() {
            let items = this.filtered_selected_comparables;
            if(!this.show_wide_set) {
                items = items.filter(obj => obj.comparable)
            }
            return items;
        },

        filtered_selected_comparables: function() {
            return [...this.selected_comparables].sort((a, b) => {
                return b.score - a.score;
            }).map((i) => {
                return {...i.sale_object, ...{
                    comparable: i.comparable,
                    score: i.score,
                    // Transaction-level, so a custom size is reflected (#4001).
                    size: i.size,
                    // Transaction-level, sale_object cannot know the analysis (#1437).
                    contains_analysis_property: i.contains_analysis_property,
                }}
            })
        },
        filtered_search_results: function() {
            return [...this.search_results].sort((a, b) => {
                return b.score - a.score;
            }).map((i) => {
                return {...i, ...{
                    comparable: this._isSelectedComparable(i),
                    score: i.score,
                }}
            })
        },
        all_transactions: function() {
            return this.filtered_selected_comparables.concat(
                this.filtered_search_results.filter(obj => {
                    return !this.selected_comparables
                        .map(i => i.sale_object.id)
                        .includes(obj.id)
                })
            )
        },
        markers: function() {
            return this.all_transactions.map(i => {
                return {
                    id: i.id,
                    gps: i.gps,
                    comparable: i.comparable,
                    score: i.score,
                }
            })
        },

        main_property_gps: function() {
            if(this.session_data && this.session_data.main_property && this.session_data.main_property.real_estate
                && this.session_data.main_property.real_estate.gps && this.session_data.main_property.real_estate.gps.lat) {
                return [
                    this.session_data.main_property.real_estate.gps.lat,
                    this.session_data.main_property.real_estate.gps.lng,
                ]
            }
            else return null;
        },
        adjustmentMatrixUrl: function() {
            let url;
            if (this.valuation_method == "cost") {
                url = `/amas/analysis/${this.session_id}/cost-method/calculation/comparable-parcels/adjustments`
            } else {
                url = `/amas/analysis/${this.session_id}/transaction/${this.transaction_type}/matrix`
            }
            return url;
        }
    }
}
</script>
